import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/admin-layout';
import { Card, Button, Alert, Badge } from '../components/ui';
import { getSlipHonorarium } from '../features/honorarium/api';
import { useInstitutionBrand } from '../components/institution-brand';
import type { SlipHonorarium } from '@app/shared';

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

type InsentifItem = {
  nama_kegiatan?: string;
  nominal?: number;
};

function Row({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 0',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: bold ? 600 : 400,
      }}
    >
      <span style={{ color: '#475569' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export function SlipHonorariumDetailPage() {
  const { pegawaiId } = useParams<{ pegawaiId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const periodeId = Number(searchParams.get('periode_id'));
  const brand = useInstitutionBrand();

  const [slip, setSlip] = useState<SlipHonorarium | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pegawaiId || Number.isNaN(periodeId)) {
      setError('Parameter pegawai atau periode tidak valid');
      setLoading(false);
      return;
    }
    getSlipHonorarium(Number(pegawaiId), periodeId)
      .then((data) => setSlip(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat slip'))
      .finally(() => setLoading(false));
  }, [pegawaiId, periodeId]);

  if (loading) {
    return (
      <AdminLayout title="Detail Slip Honorarium">
        <p>Memuat slip...</p>
      </AdminLayout>
    );
  }

  if (error || !slip) {
    return (
      <AdminLayout title="Detail Slip Honorarium">
        <Alert type="error">{error || 'Slip tidak ditemukan'}</Alert>
        <Button variant="secondary" onClick={() => navigate('/honorarium/slip')}>
          Kembali
        </Button>
      </AdminLayout>
    );
  }

  const insentif: InsentifItem[] = Array.isArray(slip.insentif) ? slip.insentif : [];

  return (
    <AdminLayout title={`Slip Honorarium – ${slip.pegawai_nama}`}>
      <div className="no-print" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <Button variant="secondary" onClick={() => navigate('/honorarium/slip')}>
          Kembali
        </Button>
        <Button onClick={() => window.print()}>Cetak Slip</Button>
      </div>

      <Card>
        <div
          style={{
            textAlign: 'center',
            marginBottom: '1.5rem',
            borderBottom: '2px solid #1e293b',
            paddingBottom: '1rem',
          }}
        >
          <h2 style={{ margin: 0 }}>{brand.nama_pesantren || 'Pesantren Madinah Ar-Rasul'}</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b' }}>Slip Honorarium Pegawai</p>
        </div>

        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <section>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Informasi Pegawai</h3>
            <Row label="Nama Pegawai" value={slip.pegawai_nama} />
            <Row label="NIP/NIK" value={slip.pegawai_nip_nik || '-'} />
            <Row label="Sekolah" value={slip.sekolah_nama || '-'} />
            <Row label="Jabatan" value={slip.jabatan_nama || '-'} />
            <Row label="Periode Honor" value={slip.kode_periode} />
            <Row
              label="Status Slip"
              value={
                <Badge color={slip.status_slip === 'final' ? 'red' : 'gray'}>
                  {slip.status_slip === 'final' ? 'Final' : 'Draft'}
                </Badge>
              }
            />
          </section>

          <section>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Rincian Honor</h3>
            <Row label="Total JP" value={slip.total_jp.toLocaleString('id-ID')} />
            <Row label="Tarif per JP" value={rupiah.format(slip.tarif_per_jp)} />
            <Row label="Honor JP" value={rupiah.format(slip.honor_jp)} />
            <Row label="Hari Hadir" value={slip.hari_hadir.toLocaleString('id-ID')} />
            <Row label="Tarif Kehadiran per Hari" value={rupiah.format(slip.tarif_kehadiran)} />
            <Row label="Honor Kehadiran" value={rupiah.format(slip.honor_kehadiran)} />
            <Row label="Honor Jabatan" value={rupiah.format(slip.honor_jabatan)} />
            <Row label="Honor Wali Kelas" value={rupiah.format(slip.honor_wali_kelas)} />
          </section>

          {insentif.length > 0 && (
            <section>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Insentif Kegiatan</h3>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.375rem',
                  overflow: 'hidden',
                }}
              >
                {insentif.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0.75rem',
                      borderBottom: idx < insentif.length - 1 ? '1px solid #e2e8f0' : undefined,
                      background: idx % 2 === 0 ? '#f8fafc' : '#ffffff',
                    }}
                  >
                    <span>{item.nama_kegiatan || `Insentif ${idx + 1}`}</span>
                    <span>{rupiah.format(Number(item.nominal) || 0)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section
            style={{
              marginTop: '0.5rem',
              padding: '1rem',
              background: '#f1f5f9',
              borderRadius: '0.375rem',
            }}
          >
            <Row label="Total Honor" value={rupiah.format(slip.total_honor)} bold />
          </section>
        </div>

        <p
          style={{
            marginTop: '2rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#64748b',
          }}
        >
          Slip ini merupakan hasil perhitungan honorarium dan tidak dapat diubah nilainya.
        </p>
      </Card>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: #fff;
          }
        }
      `}</style>
    </AdminLayout>
  );
}
